import { useEffect, useMemo, useRef, useState } from "react";
import { clsx } from "clsx";
import { useAuth } from "../lib/auth.js";
import { userRoleSubtitle } from "../lib/rbacUi.js";
import { useDevUsersList, type DevUser } from "../hooks/useDevUsers.js";

function UserIdentity({
  name,
  roles,
  domains,
  align = "right",
}: {
  name: string;
  roles: DevUser["roles"];
  domains: DevUser["domains"];
  align?: "left" | "right";
}): JSX.Element {
  return (
    <div className={clsx(align === "right" ? "text-right" : "text-left", "max-w-[10rem]")}>
      <div className="truncate text-sm font-medium leading-tight">{name}</div>
      <div className="truncate text-xs text-slate-500">{userRoleSubtitle({ roles, domains })}</div>
    </div>
  );
}

function GroupLabel({ children }: { children: string }): JSX.Element {
  return (
    <div className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
      {children}
    </div>
  );
}

/** Demo-only: switch impersonated user without signing out (AUTH_MODE=dev). */
export function DevRoleSwitcher(): JSX.Element | null {
  const { user, switchRole } = useAuth();
  const { data: devUsers, isLoading } = useDevUsersList();
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const { cartoons, pilots, others } = useMemo(() => {
    const list = [...(devUsers ?? [])].toSorted((a, b) => a.name.localeCompare(b.name));
    return {
      cartoons: list.filter((u) => u.cartoon),
      pilots: list.filter((u) => u.pilot && !u.cartoon),
      others: list.filter((u) => !u.pilot && !u.cartoon),
    };
  }, [devUsers]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (isLoading || !user || !devUsers?.length) return null;

  const groupCount = [cartoons, pilots, others].filter((g) => g.length > 0).length;
  const showHeaders = groupCount > 1;

  const renderOption = (u: DevUser) => {
    const selected = u.email === user.email;
    return (
      <button
        key={u.email}
        type="button"
        role="option"
        aria-selected={selected}
        disabled={busy}
        className={clsx(
          "block w-full px-3 py-2 text-left hover:bg-slate-50 disabled:opacity-60",
          selected && "bg-blue-50",
        )}
        onClick={async () => {
          if (selected) {
            setOpen(false);
            return;
          }
          setBusy(true);
          try {
            await switchRole(u.email);
            setOpen(false);
          } finally {
            setBusy(false);
          }
        }}
      >
        <UserIdentity name={u.name} roles={u.roles} domains={u.domains} align="left" />
      </button>
    );
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        disabled={busy}
        aria-label="Switch demo role"
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((o) => !o)}
        className={clsx(
          "flex items-center gap-1 rounded-md border border-transparent px-1 py-0.5 transition",
          "hover:border-slate-200 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand",
          busy && "opacity-60",
        )}
      >
        <UserIdentity name={user.name || user.email} roles={user.roles} domains={user.domains} />
        <span className="text-xs text-slate-400" aria-hidden>
          ▾
        </span>
      </button>
      {open && (
        <div
          role="listbox"
          aria-label="Demo roles"
          className="absolute right-0 z-50 mt-1 max-h-80 w-56 overflow-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
        >
          {showHeaders && cartoons.length > 0 ? <GroupLabel>Cartoons</GroupLabel> : null}
          {cartoons.map(renderOption)}
          {showHeaders && pilots.length > 0 ? <GroupLabel>Pilot</GroupLabel> : null}
          {pilots.map(renderOption)}
          {showHeaders && others.length > 0 ? <GroupLabel>Everyone else</GroupLabel> : null}
          {others.map(renderOption)}
        </div>
      )}
    </div>
  );
}

export function useDevAuthAvailable(): boolean {
  const { data: devUsers, isLoading } = useDevUsersList();
  return !isLoading && (devUsers?.length ?? 0) > 0;
}
